import React, { useMemo } from "react"

const EQ_BARS = 24
const ORBIT_NODES = 8
const HEX_SHARDS = [
  { x: "8%", y: "22%", s: 1, d: "0s" },
  { x: "86%", y: "18%", s: 0.75, d: "1.2s" },
  { x: "12%", y: "68%", s: 0.9, d: "0.6s" },
  { x: "78%", y: "72%", s: 1.1, d: "1.8s" },
  { x: "92%", y: "48%", s: 0.65, d: "0.3s" },
]

/**
 * Futuristic voice studio wallpaper — holographic rings, signal orbits, waveform energy.
 * Decorative only (no text).
 */
export default function SpeakingScene({ layerRef }) {
  const bars = useMemo(
    () =>
      Array.from({ length: EQ_BARS }, (_, i) => ({
        i,
        delay: `${(i % 12) * 0.12}s`,
        height: `${28 + ((i * 37) % 55)}%`,
      })),
    []
  )

  const nodes = useMemo(
    () =>
      Array.from({ length: ORBIT_NODES }, (_, i) => ({
        i,
        angle: (360 / ORBIT_NODES) * i,
        delay: `${i * 0.35}s`,
      })),
    []
  )

  return (
    <div
      ref={layerRef}
      className="speaking-shapes-layer fixed inset-0 pointer-events-none z-[1] overflow-hidden"
      aria-hidden
    >
      <div className="speaking-atmosphere" />
      <div className="speaking-atmosphere speaking-atmosphere--accent" />
      <div className="speaking-grid hidden sm:block" />
      <div className="speaking-scan hidden md:block" />

      {/* Central holographic voice core */}
      <div className="speaking-core">
        <div className="speaking-ring speaking-ring--outer" />
        <div className="speaking-ring speaking-ring--mid" />
        <div className="speaking-ring speaking-ring--inner" />
        <div className="speaking-core-pulse" />
        <div className="speaking-core-dot" />
      </div>

      {/* Orbiting signal nodes */}
      <div className="speaking-orbit hidden sm:block">
        {nodes.map((node) => (
          <span
            key={node.i}
            className="speaking-orbit-node"
            style={{
              "--orbit-angle": `${node.angle}deg`,
            }}
          >
            <span className="speaking-orbit-node-core" style={{ animationDelay: node.delay }} />
          </span>
        ))}
      </div>

      {/* Waveform ribbons */}
      <div className="speaking-ribbon speaking-ribbon--a hidden md:block" />
      <div className="speaking-ribbon speaking-ribbon--b hidden lg:block" />

      {/* Equalizer arc */}
      <div className="speaking-eq hidden sm:flex" aria-hidden>
        {bars.map((bar) => (
          <span
            key={bar.i}
            className="speaking-eq-bar"
            style={{
              "--bar-h": bar.height,
              animationDelay: bar.delay,
            }}
          />
        ))}
      </div>

      {/* Floating hex shards */}
      {HEX_SHARDS.map((shard, i) => (
        <div
          key={i}
          className={`speaking-hex ${i > 2 ? "hidden md:block" : ""}`}
          style={{
            left: shard.x,
            top: shard.y,
            "--hex-scale": shard.s,
            animationDelay: shard.d,
          }}
        />
      ))}

      {/* Soft voice flares */}
      <div className="speaking-flare speaking-flare--1" />
      <div className="speaking-flare speaking-flare--2 hidden md:block" />
    </div>
  )
}
